import apiClient from './apiService';

export interface RiskAssessmentTemplate {
  id: number;
  entity_id: number;
  assessment_code: string;
  // System-generated code (RAT-N) — null for drafts/submitted, set on approve.
  code?: string | null;
  risk_assessment_name: string;
  description?: string;
  frequency?: string;
  due_date_program?: string;
  is_active: number;
  is_approved: number;
  risk_count?: number;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  created_by?: number | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string;
  submitted_by?: number | null;
  submitted_on?: string | null;
  approved_by?: number | null;
  approved_on?: string | null;
  approval_note?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
  rejection_reason?: string | null;
}

export interface AssessmentTemplateRisk {
  id: number;
  risk_code: string;
  risk_name: string;
  category_id?: number;
}

export interface RiskAssessmentTemplateCreatePayload {
  assessment_code: string;
  risk_assessment_name: string;
  description?: string;
  frequency?: string;
  due_date_program?: string;
  risk_ids?: number[];
}

export type RiskAssessmentTemplateUpdatePayload = Partial<Omit<RiskAssessmentTemplateCreatePayload, 'risk_ids'>> & {
  is_active?: number;
  is_approved?: number;
};

const BASE = '/api/v1/rsk/assessment-templates';

export const riskAssessmentService = {
  async list(search?: string): Promise<RiskAssessmentTemplate[]> {
    const params: Record<string, string> = { limit: '200' };
    if (search) params.search = search;
    const resp = await apiClient.get(BASE, { params });
    return resp.data?.data ?? [];
  },

  async get(id: number): Promise<RiskAssessmentTemplate & { risks: AssessmentTemplateRisk[] }> {
    const resp = await apiClient.get(`${BASE}/${id}`);
    return resp.data?.data;
  },

  async create(payload: RiskAssessmentTemplateCreatePayload, action: 'draft' | 'submit' | 'approve' = 'draft'): Promise<RiskAssessmentTemplate> {
    const resp = await apiClient.post(`${BASE}?action=${action}`, payload);
    return resp.data?.data;
  },

  async update(id: number, payload: RiskAssessmentTemplateUpdatePayload): Promise<RiskAssessmentTemplate> {
    const resp = await apiClient.put(`${BASE}/${id}`, payload);
    return resp.data?.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async listRisks(id: number): Promise<AssessmentTemplateRisk[]> {
    const resp = await apiClient.get(`${BASE}/${id}/risks`);
    return resp.data?.data ?? [];
  },

  async replaceScope(id: number, riskIds: number[]): Promise<void> {
    await apiClient.put(`${BASE}/${id}/risks`, { risk_ids: riskIds });
  },

  // --- Plan 05c-04 / STAFF-11 ---
  // Cross-module source-row reader used by the RiskAssessmentResponse form
  // (05c-06) to populate one response row per attached risk. Mirrors
  // controlTestingService.listScope. NOTE: a sibling method `listRisks`
  // already wraps the same endpoint with the AssessmentTemplateRisk type;
  // `listScope` is kept as the canonical phase-05c entrypoint with a
  // permissive index-signature row shape so the form can spread additional
  // joined columns (category_name etc.) the backend may return.
  async submit(id: number): Promise<void> {
    const res = await apiClient.post(`${BASE}/${id}/submit`);
    if (!res.data.success) throw new Error(res.data.error || res.data.detail || 'Submit failed');
  },

  async approve(id: number, note?: string): Promise<void> {
    const res = await apiClient.post(`${BASE}/${id}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || res.data.detail || 'Approve failed');
  },

  async reject(id: number, reason: string): Promise<void> {
    const res = await apiClient.post(`${BASE}/${id}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || res.data.detail || 'Reject failed');
  },

  async reopen(id: number): Promise<void> {
    const res = await apiClient.post(`${BASE}/${id}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || res.data.detail || 'Reopen failed');
  },

  async listScope(templateId: number): Promise<Array<{
    risk_id: number;
    risk_code?: string | null;
    risk_name?: string | null;
    category_name?: string | null;
    [key: string]: any;
  }>> {
    const resp = await apiClient.get(`${BASE}/${templateId}/risks`);
    return resp.data?.data ?? resp.data ?? [];
  },
};

export default riskAssessmentService;
